import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe, ChevronRight } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';

const DJANGO_BASE_URL = 'https://agritrack.samperlabs.com';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { locale, toggleLocale, t } = useI18n();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#features', label: t('nav.features') },
    { href: '#pricing', label: t('nav.pricing') },
    { href: '#faq', label: 'FAQ' },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/70 backdrop-blur-xl border-b border-black/5 shadow-sm'
          : 'bg-transparent'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <a
            href="/"
            className="flex items-center group"
            data-testid="navbar-logo"
          >
            <span className="text-xl font-bold tracking-tight text-primary">
              AgriTrack
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-primary/70 hover:text-primary transition-colors duration-200"
                data-testid={`nav-link-${link.href.replace('#', '')}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Locale Switcher */}
            <button
              onClick={toggleLocale}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary/70 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors duration-200"
              data-testid="locale-switcher"
            >
              <Globe className="w-4 h-4" />
              <span className="font-mono text-xs uppercase tracking-wider">
                {locale === 'en' ? 'TR' : 'EN'}
              </span>
            </button>

            {/* Sign In */}
            <a
              href="/login"
              className="text-sm font-medium text-primary hover:text-primary/80 transition-colors duration-200"
              data-testid="sign-in-link"
            >
              {t('nav.signIn')}
            </a>

            {/* Get Started */}
            <a
              href="/register"
              data-testid="get-started-btn"
            >
              <Button className="bg-primary hover:bg-primary-light text-white font-semibold px-5 py-2 rounded-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-elevated">
                {t('nav.getStarted')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-primary"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white/95 backdrop-blur-xl border-b border-black/5"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-base font-medium text-primary/80 hover:text-primary py-2"
                  data-testid={`mobile-nav-${link.href.replace('#', '')}`}
                >
                  {link.label}
                </a>
              ))}

              <div className="pt-4 border-t border-primary/10 space-y-3">
                <button
                  onClick={toggleLocale}
                  className="flex items-center gap-2 text-sm font-medium text-primary/70"
                  data-testid="mobile-locale-switcher"
                >
                  <Globe className="w-4 h-4" />
                  <span>{locale === 'en' ? 'Türkçe' : 'English'}</span>
                </button>

                <a
                  href="/login"
                  className="block text-base font-medium text-primary py-2"
                  data-testid="mobile-sign-in"
                >
                  {t('nav.signIn')}
                </a>

                <a
                  href="/register"
                  className="block"
                  data-testid="mobile-get-started"
                >
                  <Button className="w-full bg-primary hover:bg-primary-light text-white font-semibold py-3 rounded-lg">
                    {t('nav.getStarted')}
                  </Button>
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};
